import { useEffect, useRef, useState } from "react";
import "./styles.css";
import type { Generation, SessionQuota, Style } from "./types";
import {
  fetchSession,
  fetchStyles,
  startGeneration,
  submitEmail,
  uploadImage,
  waitForPreview,
} from "./api";
import { SingleFormStep, type SingleFormStepHandle } from "./components/SingleFormStep";
import { LoadingStep } from "./components/LoadingStep";
import { PreviewStep, type PreviewStepHandle } from "./components/PreviewStep";
import { EmailGate } from "./components/EmailGate";
import { PaywallStep } from "./components/PaywallStep";
import { t } from "./i18n";

/** Widget JS API exposed via `window.MonogatariWidget` when running inside a
 *  Shopify PDP. The PDP Liquid wires its Preview / Add to Cart buttons to
 *  these methods so customers see one set of CTAs (the PDP buttons), not a
 *  duplicate pair inside the widget. */
interface MonogatariWidgetApi {
  submitForm: () => boolean;
  addToCart: () => Promise<string | undefined>;
}

/** Detail emitted with `monogatari:state` whenever the widget's step or
 *  readiness flips. PDP Liquid listens to enable/disable + relabel buttons. */
interface MonogatariWidgetState {
  step: "missing_style" | "single_form" | "loading" | "preview" | "email_gate" | "paywall";
  canPreview: boolean;  // Form has photo + name AND we're at single_form
  canAddCart: boolean;  // Preview ready AND not already busy
  busy: boolean;        // step === "loading"
}

// Widget-only app. The React bundle is mounted exclusively inside Shopify
// — either on a PDP via `product.monogatari.liquid` (styleId from
// `product.metafields.custom.style_id`) or on a custom page via the
// section's `style_id` setting.
//
// Flow: single_form (upload + name in one card) → loading → preview.
// Older UploadStep/CropStep/NameStep cards were collapsed into SingleFormStep
// to match the wonderme PDP pattern (one compact form, no friction). Their
// files are kept in the repo as reference but no longer imported.

type Step =
  | "missing_style"
  | "single_form"
  | "loading"
  | "preview"
  | "email_gate"
  | "paywall";

const BROWSE_COLLECTIONS_URL = "/collections/all";

/** Read `data-style-id` from the React mount element. Set by the PDP Liquid
 *  template from `product.metafields.custom.style_id`, or by the section's
 *  `style_id` setting on standalone pages. */
function getMountStyleId(): string | null {
  if (typeof document === "undefined") return null;
  const root = document.getElementById("root");
  const raw = root?.dataset.styleId?.trim();
  return raw && raw.length > 0 ? raw : null;
}

export function App() {
  // Compute initial step from the DOM synchronously so the very first paint
  // already shows the right UI — no flash of "missing_style" before the
  // useEffect below swaps in "single_form". Both reads are cheap DOM lookups.
  const initialMountStyleId = getMountStyleId();
  const initialUrlSlug =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("template")
      : null;
  const initialStyleId = initialMountStyleId ?? initialUrlSlug;

  const [step, setStep] = useState<Step>(
    initialStyleId ? "single_form" : "missing_style",
  );
  const [selectedStyleId, setSelectedStyleId] = useState<string | null>(
    initialStyleId,
  );
  // Widget mode = mounted inside a PDP (data-style-id from product metafield).
  // Drives: hiding internal buttons, exposing window.MonogatariWidget API.
  const widgetMode = initialMountStyleId !== null;
  const [formReady, setFormReady] = useState(false);
  const formRef = useRef<SingleFormStepHandle>(null);
  const previewRef = useRef<PreviewStepHandle>(null);

  const [dadName, setPetName] = useState<string>("");
  const [uploadId, setUploadId] = useState<string | null>(null);

  const [styles, setStyles] = useState<Style[]>([]);

  const [generation, setGeneration] = useState<Generation | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [quota, setQuota] = useState<SessionQuota | null>(null);

  // Initial load: styles + session quota.
  useEffect(() => {
    fetchStyles()
      .then((s) => setStyles(s))
      .catch((e) => console.warn("[styles] fetch failed:", e));
    fetchSession()
      .then((s) => setQuota(s.quota))
      .catch((e) => console.warn("[session] fetch failed:", e));
  }, []);

  // Validate the optimistic initialStyleId against the loaded catalog. If it
  // doesn't match a real style, fall back to missing_style. The render-on-
  // mount above already shows the correct UI in the common case; this only
  // catches misconfigured PDPs / typo'd metafields.
  useEffect(() => {
    if (styles.length === 0) return;
    if (!selectedStyleId) return;
    const match = styles.find((s) => s.id === selectedStyleId);
    if (!match) {
      console.warn(
        `[widget] selectedStyleId="${selectedStyleId}" did not match any loaded style`,
      );
      setSelectedStyleId(null);
      setStep("missing_style");
    }
  }, [styles, selectedStyleId]);

  // Expose the imperative API + emit a state event for the PDP's external
  // Preview / Add to Cart buttons. Standalone mode keeps it disabled — no
  // PDP buttons to drive.
  useEffect(() => {
    if (!widgetMode) return;
    const api: MonogatariWidgetApi = {
      submitForm: () => formRef.current?.submit() ?? false,
      addToCart: async () => {
        const fn = previewRef.current?.addToCart;
        if (!fn) return undefined;
        return fn();
      },
    };
    (window as unknown as { MonogatariWidget?: MonogatariWidgetApi }).MonogatariWidget = api;

    const stateDetail: MonogatariWidgetState = {
      step,
      canPreview: step === "single_form" && formReady,
      canAddCart: step === "preview" && generation !== null,
      busy: step === "loading",
    };
    window.dispatchEvent(new CustomEvent("monogatari:state", { detail: stateDetail }));

    return () => {
      delete (window as unknown as { MonogatariWidget?: MonogatariWidgetApi }).MonogatariWidget;
    };
  }, [widgetMode, step, formReady, generation]);

  /** Single-form submit handler: caller provides the file and name in one
   *  shot. We upload + kick off /generate immediately. The local blob URL is
   *  owned by SingleFormStep — App doesn't need to track it. */
  async function handleFormSubmit(file: File, _fileUrl: string, name: string) {
    if (!selectedStyleId) {
      setStep("missing_style");
      return;
    }
    setPetName(name);
    setStep("loading");

    let id = uploadId;
    if (!id) {
      try {
        const upload = await uploadImage(file);
        id = upload.upload_id;
        setUploadId(id);
      } catch (e) {
        setGenError(e instanceof Error ? e.message : String(e));
        setStep("single_form");
        return;
      }
    }
    // Pass `name` explicitly — state setter above is async, so reading from
    // `dadName` inside tryGenerate would see the previous (empty) value.
    await tryGenerate(selectedStyleId, id, name);
  }

  // Runs the actual /generate call; returns true if generation completed,
  // false if the quota gate intervened (caller stays put — gate UI takes over).
  async function tryGenerate(
    styleId: string,
    explicitUploadId?: string,
    explicitDadName?: string,
  ): Promise<boolean> {
    const id = explicitUploadId ?? uploadId;
    if (!id) {
      setStep("single_form");
      return false;
    }
    setSelectedStyleId(styleId);
    setGenError(null);
    setStep("loading");
    const nameToSend = explicitDadName ?? dadName;
    try {
      const result = await startGeneration({
        upload_id: id,
        style_id: styleId,
        dad_name: nameToSend,
      });

      if (result.kind === "quota_exhausted") {
        setQuota(result.quota);
        setStep(result.quota.next_step === "paywall" ? "paywall" : "email_gate");
        return false;
      }

      // 180s covers: Fly cold-start (~10s) + Fal face-swap queue (~30-60s
      // peak) + a margin. If this still times out, status stays "running"
      // server-side — check Fly logs for the Fal error.
      const ready = await waitForPreview(result.generation.id, {
        timeoutMs: 180_000,
        intervalMs: 1500,
      });
      setGeneration(ready);
      setStep("preview");
      const fresh = await fetchSession().catch(() => null);
      if (fresh) setQuota(fresh.quota);
      return true;
    } catch (e) {
      setGenError(e instanceof Error ? e.message : String(e));
      setStep("single_form");
      return false;
    }
  }

  async function handleEmailSubmit(email: string) {
    const info = await submitEmail({
      email,
      ...(dadName && { dad_name: dadName }),
    });
    setQuota(info.quota);
    if (selectedStyleId) {
      await tryGenerate(selectedStyleId);
    }
  }

  function restart() {
    // Keep selectedStyleId — we're on this template's PDP / page.
    setStep("single_form");
    setPetName("");
    setUploadId(null);
    setGeneration(null);
    setGenError(null);
  }

  const quotaCopy =
    quota && quota.remaining > 0
      ? quota.generations_used === 0
        ? t("app.quota.first", { n: quota.remaining })
        : quota.remaining === 1
          ? t("app.quota.left.one")
          : t("app.quota.left.many", { n: quota.remaining })
      : null;

  return (
    <div className="app-root theme-light widget-mode">
      {quotaCopy && step !== "missing_style" && (
        <div className="quota-banner">
          <span
            className={
              "quota-chip" + (quota && quota.remaining === 1 ? " warn" : "")
            }
          >
            {quotaCopy}
          </span>
        </div>
      )}

      {genError && step !== "loading" && (
        <div className="error-banner" role="alert">{genError}</div>
      )}

      <main className="page-main" style={{ maxWidth: "560px" }}>
        {step === "missing_style" && (
          <div className="card missing-style">
            <h2 className="missing-style__title">{t("app.missing_style.title")}</h2>
            <p className="missing-style__body">{t("app.missing_style.body")}</p>
            <a className="btn btn-primary missing-style__cta" href={BROWSE_COLLECTIONS_URL}>
              {t("app.missing_style.cta")}
            </a>
          </div>
        )}

        {step === "single_form" && (
          <SingleFormStep
            ref={formRef}
            onSubmit={handleFormSubmit}
            onReadyChange={setFormReady}
            hideSubmitButton={widgetMode}
          />
        )}

        {step === "loading" && (
          <LoadingStep
            dadName={dadName}
            styleLabel={
              styles.find((s) => s.id === selectedStyleId)?.label ?? "legendary"
            }
            widgetMode={widgetMode}
          />
        )}

        {step === "preview" && generation && (
          <PreviewStep
            ref={previewRef}
            generation={generation}
            style={styles.find((s) => s.id === generation.style_id)!}
            onRestart={restart}
            hideAddToCartButton={widgetMode}
          />
        )}

        {step === "email_gate" && (
          <EmailGate
            bonus={quota ? Math.max(2, quota.limit - quota.generations_used) : 2}
            dadName={dadName}
            onSubmit={handleEmailSubmit}
            onSkip={() => (generation ? setStep("preview") : setStep("single_form"))}
          />
        )}

        {step === "paywall" && (
          <PaywallStep
            dadName={dadName}
            onCheckout={() => alert("Product mockups land in week 3.")}
            onRestart={restart}
          />
        )}
      </main>
    </div>
  );
}
